import React, {useState} from 'react';
import {PieChart, Pie, BarChart, Cell, ResponsiveContainer, CartesianGrid, Tooltip, XAxis, YAxis, Legend, Bar, Label, LabelList} from 'recharts';
import Layout from '../../Shared/Layout';
import {Container, } from "@material-ui/core";
import i18n, { Trans } from "../../utils/i18n";
import classNames from "classnames";
import AvoidedEmissions from "./AvoidedEmissions";
import ProductCarbonFootprint from "./ProductCarbonFootprint";
import AvoidedEmissionsEnterprise from "./AvoidedEmissionsEnterprise";

function PieChartWrapper({ data01, data02, data03, COLORS, COLORS2, COLORS3 }) {
    return (
        <ResponsiveContainer width="100%" height="100%">
            <PieChart width={400} height={300}>
                <Pie
                    startAngle={-90}
                    endAngle={270}
                    data={data01} dataKey="value" cx="50%" cy="50%" innerRadius={70} outerRadius={90} fill="#FF4F50">
                    {data01.map((entry, index) => (
                        <Cell key={`cell1-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                    label
                </Pie>
                <Pie
                    startAngle={-90}
                    endAngle={270}
                    data={data02} dataKey="value" cx="50%" cy="50%" innerRadius={100} outerRadius={120} fill="#175467">
                    {data02.map((entry, index) => (
                        <Cell key={`cell2-${index}`} fill={COLORS2[index % COLORS2.length]} />
                    ))}
                </Pie>
                <Pie
                    startAngle={-90}
                    endAngle={270}
                    data={data03} dataKey="value" cx="50%" cy="50%" innerRadius={130} outerRadius={150} fill="#175467">
                    {data03.map((entry, index) => (
                        <Cell key={`cell3-${index}`} fill={COLORS3[index % COLORS3.length]} />
                    ))}
                </Pie>
            </PieChart>
        </ResponsiveContainer>
    );
}

export function formatUnit(value, unit = '') {
    if(unit === 'kg' && value > 1000) {
        value = value / 1000;
        unit = 't';
    }
    if(unit === 'balloons' && value > 10000) {
        value = value / 1000;
        unit = 'k balloons';
    }
    value = value.toLocaleString('en-US', { maximumFractionDigits: 0, });
    return `${value} ${unit}`;
}

export default function Emissions({ result, isEnterprise, }) {
    const entry = result.models[0];
    const device = entry.product;
    const coEmitReplacementKg = formatUnit(Math.round(result.total_device_pcf), 'kg');
    const fault = entry.problem;
    const km_of_car_ride = formatUnit(result.km_of_car_ride,'km');
    const balloons = formatUnit(result.balloons, 'balloons');
    const trees_compensation = formatUnit(result.trees_compensation);
    const avoided_emission = formatUnit(Math.round(result.total_device_pcf) - Math.round(result.repair_emission), 'kg');
    const trees_text = trees_compensation == 1 ? 'tree' : 'trees';
    const [activeTab, setActiveTab] = useState('avoidedEmissions');
    const tabs = [
        { name: 'Avoided Emissions', id: 'avoidedEmissions', current: activeTab === 'avoidedEmissions' },
        { name: 'Product Carbon Footprint', id: 'carbonFootprint', current: activeTab === 'carbonFootprint' },
    ];


    return (
        <>
            <div className={'bg-[#FCFFFF] -mt-12 min-h-[37.5rem] bg-cover bg-center pt-16'} style={{ backgroundImage: `url(/images/emission_bg_raw.jpeg)`}}>
                <Container maxWidth="md">
                    <div className={'backdrop-blur bg-white/50 p-9 w-full rounded-lg mt-9 lg:mt-16'}>
                        <p className={'m-0 text-2xl font-semibold text-gray-700'}>
                            {
                                isEnterprise ?
                                <Trans i18nKey="emission.repairingWillSave">
                                    Repairing the specified devices will save
                                </Trans> :
                                <Trans i18nKey="emission.repairingYourDevice" fault={fault}>
                                    Repairing your {{ fault }} will save
                                </Trans>
                            }
                        </p>
                        <p className={'m-0  text-5xl font-bold text-gray-700'}>
                            <span style={{
                                background: "linear-gradient(90deg, #FF4F50 0%, #F13B5F 100%)",
                                backgroundClip: "text",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }} >
                                { avoided_emission } </span>{' '}
                            of CO2e
                        </p>
                        <p className={'m-0 text-2xl font-semibold text-gray-700 mt-3'}>
                            {
                                isEnterprise ?
                                <Trans i18nKey="emission.inComparisonEnterprise">
                                    In comparison, replacing these devices would emit {{ coEmitReplacementKg }} of CO2e
                                </Trans> :
                                <Trans i18nKey="emission.inComparison" device={device}>
                                    In comparison, replacing your {{ device }} would emit {{ coEmitReplacementKg }} of CO2e
                                </Trans>
                            }
                        </p>
                    </div>
                </Container>
            </div>
            <div className={'bg-[#7A8493] py-16 text-center text-white'}>
                <Container maxWidth="lg">
                    <p className={'text-3xl font-semibold'}>
                        Your CO2e avoidance is roughly equivalent to ...
                    </p>
                    <div className={'grid grid-cols-12 gap-6'}>
                        <div className={'col-span-12 sm:col-span-6 md:col-span-4'}>
                            <img src={'/images/b2x_carbon/car.jpg'} alt={'car'} className={'w-full rounded-2xl'}/>
                            <p className={'text-3xl font-semibold'}>
                                <Trans i18nKey="carbon.kmCarRide" km_of_car_ride={km_of_car_ride}>
                                    a {{ km_of_car_ride }} ride with a medium sized gasoline car.
                                </Trans>
                            </p>
                        </div>
                        <div className={'col-span-12 sm:col-span-6 md:col-span-4'}>
                            <img src={'/images/b2x_carbon/balloons.png'} alt={'ballons'} className={'w-full rounded-2xl'}/>
                            <p className={'text-3xl font-semibold'}>
                                <Trans i18nKey="carbon.balloonEquivalent" balloons={balloons}>filling {{ balloons }} of CO2e.</Trans>
                            </p>
                        </div>
                        <div className={'col-span-12 sm:col-span-6 md:col-span-4'}>
                            <img src={'/images/b2x_carbon/forest.jpg'} alt={'trees'} className={'w-full rounded-2xl'}/>
                            <p className={'text-3xl font-semibold'}>
                                <Trans i18nKey="carbon.treeCompensate" trees_compensation={trees_compensation}>
                                    the CO2e compensation of {{ trees_compensation }} beech {{trees_text}} per year.
                                </Trans>
                            </p>
                        </div>
                    </div>
                </Container>
            </div>
            <div className={'bg-[#FCFFFF] py-16 text-center text-black'}>
                {
                    !!isEnterprise &&
                    <div className={'max-w-7xl mx-auto mb-9'}>
                        <div className="sm:hidden">
                            <label htmlFor="tabs" className="sr-only">
                                Select a tab
                            </label>
                            {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
                            <select
                                id="tabs"
                                name="tabs"
                                className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                                defaultValue={tabs.find((tab) => tab.current).name}
                            >
                                {tabs.map((tab) => (
                                    <option key={tab.name}>{tab.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="hidden sm:block">
                            <nav className="isolate flex divide-x divide-gray-200 rounded-lg shadow-md border-t border-y border-gray-200" aria-label="Tabs">
                                {tabs.map((tab, tabIdx) => (
                                    <button
                                        key={tab.name}
                                        className={classNames(
                                            tab.current ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700',
                                            tabIdx === 0 ? 'rounded-l-lg' : '',
                                            tabIdx === tabs.length - 1 ? 'rounded-r-lg' : '',
                                            'group cursor-pointer no-underline relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10'
                                        )}
                                        aria-current={tab.current ? 'page' : undefined}
                                        onClick={() => {
                                            setActiveTab(tab.id);
                                        }}
                                    >
                                        <span>{tab.name}</span>
                                        <span
                                            aria-hidden="true"
                                            className={classNames(
                                                tab.current ? 'bg-secondary' : 'bg-transparent',
                                                'absolute inset-x-0 bottom-0 h-1'
                                            )}
                                        />
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>
                }

                <Container maxWidth="lg">
                    {
                        !isEnterprise && activeTab === 'avoidedEmissions' &&
                        <AvoidedEmissions
                            result={result}
                        />
                    }
                    {
                        !!isEnterprise && activeTab === 'avoidedEmissions' &&
                        <AvoidedEmissionsEnterprise
                            result={result}
                        />
                    }
                    {
                        !!isEnterprise && activeTab === 'carbonFootprint' &&
                        <ProductCarbonFootprint
                            result={result}
                        />
                    }
                    {
                    !isEnterprise &&
                        <div
                            className="mt-16 w-full max-w-xl px-10 py-8 bg-white bg-opacity-80 rounded-2xl shadow flex-col justify-start items-start gap-6 inline-flex text-left">
                            <div className="flex-col justify-start items-center gap-4 flex">
                                <p className={'m-0 leading-10'}>
                                    <span className="text-black text-4xl font-bold">
                                        <Trans i18nKey="carbon.salesContact.1">
                                        Did you like our calculator? Try our version for
                                        </Trans>
                                    </span>{' '}
                                    <span className="text-red-500 text-4xl font-bold">enterprises</span>
                                </p>
                                <div className="self-stretch text-black text-base font-normal">
                                    <Trans i18nKey="carbon.salesContact.2">
                                        Access the enterprise tool to calculate simultaneously a large amount of
                                        devices and access all the data
                                    </Trans>
                                </div>
                            </div>
                            <div
                                className="px-[22px] py-2 rounded-[7px] border border-slate-600 justify-center items-center gap-2.5 inline-flex hover:bg-gray-900 hover:text-white">
                                <div className="justify-start items-center gap-2 flex">
                                    <a href={'https://www.b2x.com/demo'}
                                       className="text-inherit no-underline text-base font-bold ">
                                        <Trans i18nKey={'carbon.getInTouch'}>
                                            Get in touch
                                        </Trans>
                                    </a>
                                </div>
                            </div>
                        </div>
                    }
                </Container>
            </div>
        </>
    )
}


Emissions.layout = page => <Layout children={page} />;
